import React, { useMemo, useState } from "react"
import ModalPortal from "../../components/Modal/ModalPortal"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import ButtonIcon from "../../components/Button/Button"
import {
  faClose,
  faCircleInfo,
  faUsers,
  faUserTie,
  faSliders,
  faBuilding,
  faMapLocationDot,
  faPhone,
  faBox,
  faTruck,
  faStore,
  faEnvelope,
  faGlobe,
  faLink,
  faBoxesStacked,
  faClock,
  faMotorcycle,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { useTranslation } from "react-i18next"
import { AdminSubscriptionMonitoringDetails } from "../../Objects/AdminDashboard/AdminSubscriptionMonitoringDetails"
import { convertToLocalMoney } from "../../Objects/Generics/Helps"
import { MetricCard } from "../../components/MetricCard/MetricCard"
import { SimpleToggle } from "../../components/SimpleToggle/SimpleToggle"

type ESubscriptionMonitoringDetailTab = "overview" | "settings" | "customers" | "employees" | "couriers"

interface SubscriptionMonitoringDetailsPopupProps {
  details: AdminSubscriptionMonitoringDetails
  onClose: () => void
  onToggleTestOrDemo: (value: boolean) => void
}

const getBooleanText = (value: boolean, t: (key: string) => string) =>
  value ? t("admin_dashboard.subscription_details.yes") : t("admin_dashboard.subscription_details.no")

const getStoreServiceTypeLabel = (value: string, t: (key: string) => string) => {
  if (value === "Delivery") return t("admin_dashboard.subscription_details.store_service_delivery")
  if (value === "Local") return t("admin_dashboard.subscription_details.store_service_local")
  if (value === "Both") return t("admin_dashboard.subscription_details.store_service_both")
  return value || "-"
}

const getDeliveryFulfillmentTypeLabel = (value: string, t: (key: string) => string) => {
  if (value === "DeliveryOnly") return t("admin_dashboard.subscription_details.delivery_fulfillment_delivery")
  if (value === "PickupOnly") return t("admin_dashboard.subscription_details.delivery_fulfillment_pickup")
  if (value === "Both") return t("admin_dashboard.subscription_details.delivery_fulfillment_both")
  return value || "-"
}

const getEmployeeStatusLabel = (value: string, t: (key: string) => string) => {
  if (value === "Active") return t("admin_dashboard.subscription_details.status_active")
  if (value === "Disabled") return t("admin_dashboard.subscription_details.status_disabled")
  return value || "-"
}

const getCourierStatusLabel = (value: string, t: (key: string) => string) => {
  if (value === "Online") return t("admin_dashboard.subscription_details.courier_status_online")
  if (value === "Offline") return t("admin_dashboard.subscription_details.courier_status_offline")
  return value || "-"
}

const getCourierVehicleLabel = (value: string, t: (key: string) => string) => {
  if (value === "Motorcycle") return t("admin_dashboard.subscription_details.courier_vehicle_motorcycle")
  if (value === "Car") return t("admin_dashboard.subscription_details.courier_vehicle_car")
  if (value === "Bike") return t("admin_dashboard.subscription_details.courier_vehicle_bike")
  if (value === "OnFoot") return t("admin_dashboard.subscription_details.courier_vehicle_on_foot")
  return value || "-"
}

const getCustomerDebitStatusLabel = (value: string, t: (key: string) => string) => {
  if (value === "WithInDebit") return t("admin_dashboard.subscription_details.customer_with_debit")
  if (value === "WithOutDebit") return t("admin_dashboard.subscription_details.customer_without_debit")
  return value || "-"
}

export const SubscriptionMonitoringDetailsPopup: React.FC<SubscriptionMonitoringDetailsPopupProps> = ({
  details,
  onClose,
  onToggleTestOrDemo,
}) => {
  const { t } = useTranslation("frontend")

  const [tab, setTab] = useState<ESubscriptionMonitoringDetailTab>("overview")

  const metricsCards = useMemo(
    () => [
      {
        label: t("admin_dashboard.subscription_details.total_products"),
        value: details.catalogMetrics.totalProducts,
        icon: faBox,
      },
      {
        label: t("admin_dashboard.subscription_details.orders_count"),
        value: details.catalogMetrics.ordersCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.manual_external_orders_count"),
        value: details.catalogMetrics.manualExternalOrdersCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.customers_count"),
        value: details.catalogMetrics.customersCount,
        icon: faUsers,
      },
      {
        label: t("admin_dashboard.subscription_details.employees_count"),
        value: details.catalogMetrics.employeesCount,
        icon: faUserTie,
      },
      {
        label: t("admin_dashboard.subscription_details.couriers_count"),
        value: details.catalogMetrics.couriersCount,
        icon: faTruck,
      },
      {
        label: t("admin_dashboard.subscription_details.categories_count"),
        value: details.catalogMetrics.categoriesCount,
        icon: faSliders,
      },
    ],
    [details, t],
  )

  const catalogItems = useMemo(
    () => [
      {
        label: t("admin_dashboard.subscription_details.products_count"),
        value: details.catalogMetrics.productsCount,
        icon: faBox,
      },
      {
        label: t("admin_dashboard.subscription_details.catalogItems_count"),
        value: details.catalogMetrics.catalogItemsCount,
        icon: faBox,
      },
      {
        label: t("admin_dashboard.subscription_details.catalog_products_count"),
        value: details.catalogMetrics.catalogProductsCount,
        icon: faBox,
      },
      {
        label: t("admin_dashboard.subscription_details.combos_count"),
        value: details.catalogMetrics.combosCount,
        icon: faBox,
      },
      {
        label: t("admin_dashboard.subscription_details.extra_services_count"),
        value: details.catalogMetrics.extraServicesCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.coupons_count"),
        value: details.catalogMetrics.couponsCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.promotions_count"),
        value: details.catalogMetrics.promotionsCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.servicePoints_count"),
        value: details.catalogMetrics.servicePointsCount,
        icon: faStore,
      },
      {
        label: t("admin_dashboard.subscription_details.active_products_count"),
        value: details.catalogMetrics.activeProductsCount,
        icon: faBoxesStacked,
      },
      {
        label: t("admin_dashboard.subscription_details.inactive_products_count"),
        value: details.catalogMetrics.inactiveProductsCount,
        icon: faBoxesStacked,
      },
      {
        label: t("admin_dashboard.subscription_details.stock_control_products_count"),
        value: details.catalogMetrics.productsWithStockControlCount,
        icon: faBoxesStacked,
      },
      {
        label: t("admin_dashboard.subscription_details.out_of_stock_products_count"),
        value: details.catalogMetrics.outOfStockProductsCount,
        icon: faBoxesStacked,
      },
      {
        label: t("admin_dashboard.subscription_details.preparation_time_products_count"),
        value: details.catalogMetrics.productsWithPreparationTimeCount,
        icon: faClock,
      },
      {
        label: t("admin_dashboard.subscription_details.need_fulfillment_products_count"),
        value: details.catalogMetrics.productsNeedFulfillmentCount,
        icon: faBoxesStacked,
      },
      {
        label: t("admin_dashboard.subscription_details.reservation_enabled_products_count"),
        value: details.catalogMetrics.reservationEnabledProductsCount,
        icon: faClock,
      },
    ],
    [details, t],
  )

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={`${defaultStyle.alert2} max-w-6xl rounded-lg border border-slate-200 bg-white shadow-2xl`}>
            <TitlePageResponsiveIconSmall
              title={`${t("admin_dashboard.subscription_details.title")}: ${details.companyName}`}
              icon={faCircleInfo}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={onClose}
                  descriptionKey="interactionDescriptions:pages.adminDashboard.subscriptionMonitoringDetailsPopup.button.onClose"
                />
              }
            />

            <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
              <div className="mt-4">
                <SimpleToggle
                  title={t("admin_dashboard.subscription_details.test_or_demo_toggle")}
                  value={details.isTestOrDemoCompany}
                  onChange={onToggleTestOrDemo}
                  animationDuration="0.35s"
                  descriptionKey="interactionDescriptions:pages.adminDashboard.subscriptionMonitoringDetailsPopup.toggle.testOrDemoToggle"
                />
              </div>
            </Animate>

            {details.isTestOrDemoCompany && (
              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <div className="mt-4 rounded-lg border border-slate-300 bg-slate-50 p-4 text-sm font-semibold text-slate-700">
                  {t("admin_dashboard.subscription_details.test_or_demo_notice")}
                </div>
              </Animate>
            )}

            {!details.isTestOrDemoCompany && (
              <>
                <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                  <div className="mt-3 rounded-lg border border-slate-300 bg-slate-50 p-2">
                    <div className="flex flex-wrap gap-2">
                      {[
                        {
                          key: "overview",
                          label: t("admin_dashboard.subscription_details.overview_button"),
                          icon: faBuilding,
                        },
                        {
                          key: "settings",
                          label: t("admin_dashboard.subscription_details.settings_button"),
                          icon: faSliders,
                        },
                        {
                          key: "customers",
                          label: t("admin_dashboard.subscription_details.customers_button"),
                          icon: faUsers,
                        },
                        {
                          key: "employees",
                          label: t("admin_dashboard.subscription_details.employees_button"),
                          icon: faUserTie,
                        },
                        {
                          key: "couriers",
                          label: t("admin_dashboard.subscription_details.couriers_button"),
                          icon: faMotorcycle,
                        },
                      ].map((item) => (
                        <button
                          key={item.key}
                          className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-bold transition ${
                            tab === item.key
                              ? "border-orange-600 bg-orange-600 text-white"
                              : "border-slate-300 bg-white text-slate-800 hover:bg-slate-100"
                          }`}
                          onClick={() => setTab(item.key as ESubscriptionMonitoringDetailTab)}
                          data-interaction-description-key="interactionDescriptions:pages.adminDashboard.subscriptionMonitoringDetailsPopup.button.setTabItemKeyAsESubscriptionMonitoringDetailTab"
                        >
                          <FontAwesomeIcon icon={item.icon} />
                          {item.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </Animate>

                {tab === "overview" && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                    <div className="mt-4 space-y-4">
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                        {metricsCards.map((item) => (
                          <MetricCard key={item.label} title={item.label} value={`${item.value}`} icon={item.icon} />
                        ))}
                      </div>

                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                          <h4 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                            <FontAwesomeIcon icon={faBuilding} className="text-orange-600" />

                            {t("admin_dashboard.subscription_details.profile_title")}
                          </h4>

                          <div className="mt-3 space-y-2 text-sm text-slate-800">
                            <p>
                              <strong>CNPJ:</strong> {details.profile.cnpj || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.social_reason")}:</strong>{" "}
                              {details.profile.socialReason || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.state_registration")}:</strong>{" "}
                              {details.profile.stateRegistration || "-"}
                            </p>

                            <p>
                              <strong>Slug:</strong> {details.profile.slug || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.timezone")}:</strong>{" "}
                              {details.profile.timeZoneId || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.public_message")}:</strong>{" "}
                              {details.profile.companyMessage || "-"}
                            </p>
                          </div>
                        </div>

                        <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                          <h4 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                            <FontAwesomeIcon icon={faMapLocationDot} className="text-orange-600" />
                            {t("admin_dashboard.subscription_details.address_title")}
                          </h4>

                          <div className="mt-3 space-y-2 text-sm text-slate-800">
                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_label")}:</strong>{" "}
                              {details.address.label || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_street")}:</strong>{" "}
                              {details.address.street || "-"}, {details.address.number || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_complement")}:</strong>{" "}
                              {details.address.complement || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_neighborhood")}:</strong>{" "}
                              {details.address.neighborhood || "-"}
                            </p>

                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_city_state")}:</strong>{" "}
                              {details.address.city || "-"} / {details.address.state || "-"}
                            </p>

                            <p>
                              <strong>CEP:</strong> {details.address.zipCode || "-"}
                            </p>
                            <p>
                              <strong>{t("admin_dashboard.subscription_details.address_country")}:</strong>{" "}
                              {details.address.country || "-"}
                            </p>

                            <p>
                              <strong>Lat/Lng:</strong> {details.address.latitude ?? "-"} /{" "}
                              {details.address.longitude ?? "-"}
                            </p>
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                        <h4 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                          <FontAwesomeIcon icon={faLink} className="text-orange-600" />
                          {t("admin_dashboard.subscription_details.contact_title")}
                        </h4>

                        <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-slate-800 md:grid-cols-2">
                          <p>
                            <FontAwesomeIcon icon={faUserTie} className="mr-2 text-slate-500" />
                            {details.contact.ownerName || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-slate-500" />
                            {details.contact.ownerEmail || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faPhone} className="mr-2 text-slate-500" />
                            {details.contact.ownerPhone || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faGlobe} className="mr-2 text-slate-500" />
                            Instagram: {details.contact.instagramUrl || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faGlobe} className="mr-2 text-slate-500" />
                            Facebook: {details.contact.facebookUrl || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faGlobe} className="mr-2 text-slate-500" />
                            TikTok: {details.contact.tiktokUrl || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faPhone} className="mr-2 text-slate-500" />
                            WhatsApp: {details.contact.whatsappPhone || "-"}
                          </p>

                          <p>
                            <FontAwesomeIcon icon={faGlobe} className="mr-2 text-slate-500" />
                            URL WhatsApp: {details.contact.whatsappUrl || "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Animate>
                )}

                {tab === "settings" && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-2">
                      <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                        <h4 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                          <FontAwesomeIcon icon={faSliders} className="text-orange-600" />
                          {t("admin_dashboard.subscription_details.configuration_title")}
                        </h4>

                        <div className="mt-3 grid grid-cols-1 gap-2">
                          <div className="rounded-lg border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faStore} className="mr-2 text-orange-600" />
                            <strong>{t("admin_dashboard.subscription_details.store_service_type")}:</strong>{" "}
                            {getStoreServiceTypeLabel(details.configuration.storeServiceType, t)}
                          </div>

                          <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faTruck} className="mr-2 text-blue-600" />
                            <strong>{t("admin_dashboard.subscription_details.delivery_fulfillment_type")}:</strong>{" "}
                            {getDeliveryFulfillmentTypeLabel(details.configuration.deliveryFulfillmentType, t)}
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faTruck} className="mr-2 text-slate-600" />
                            <strong>{t("admin_dashboard.subscription_details.delivery_enabled")}:</strong>{" "}
                            {getBooleanText(details.configuration.isDeliveryEnabled, t)}
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faStore} className="mr-2 text-slate-600" />
                            <strong>{t("admin_dashboard.subscription_details.pickup_enabled")}:</strong>{" "}
                            {getBooleanText(details.configuration.isPickupEnabled, t)}
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faSliders} className="mr-2 text-slate-600" />
                            <strong>
                              {t("admin_dashboard.subscription_details.delivery_notification_enabled")}:
                            </strong>{" "}
                            {getBooleanText(details.configuration.isDeliveryOrderNotificationEnabled, t)}
                          </div>

                          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faSliders} className="mr-2 text-emerald-600" />
                            <strong>{t("admin_dashboard.subscription_details.free_shipping_enabled")}:</strong>{" "}
                            {getBooleanText(details.configuration.isFreeShippingEnabled, t)} |{" "}
                            <strong>{t("admin_dashboard.subscription_details.free_shipping_minimum")}:</strong>{" "}
                            {convertToLocalMoney(details.configuration.freeShippingMinimumOrderValue)}
                          </div>

                          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faSliders} className="mr-2 text-amber-600" />
                            <strong>{t("admin_dashboard.subscription_details.fixed_shipping_enabled")}:</strong>{" "}
                            {getBooleanText(details.configuration.isFixedShippingFeeEnabled, t)} |{" "}
                            <strong>{t("admin_dashboard.subscription_details.fixed_shipping_value")}:</strong>{" "}
                            {convertToLocalMoney(details.configuration.fixedShippingFeeValue)}
                          </div>

                          <div className="rounded-lg border border-purple-200 bg-purple-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faMapLocationDot} className="mr-2 text-purple-600" />
                            <strong>{t("admin_dashboard.subscription_details.local_radius")}:</strong>{" "}
                            {details.configuration.localServiceRadiusMeters}m
                          </div>

                          <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-slate-800">
                            <FontAwesomeIcon icon={faClock} className="mr-2 text-rose-600" />
                            <strong>{t("admin_dashboard.subscription_details.vacation_enabled")}:</strong>{" "}
                            {getBooleanText(details.configuration.isVacationEnabled, t)} |{" "}
                            <strong>{t("admin_dashboard.subscription_details.vacation_range")}:</strong>{" "}
                            {details.configuration.vacationStartDate || "-"} →{" "}
                            {details.configuration.vacationEndDate || "-"}
                          </div>
                        </div>
                      </div>

                      <div className="rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                        <h4 className="flex items-center gap-2 text-base font-extrabold text-slate-900">
                          <FontAwesomeIcon icon={faStore} className="text-orange-600" />
                          {t("admin_dashboard.subscription_details.catalog_title")}
                        </h4>

                        <div className="mt-3 space-y-2 text-sm text-slate-800">
                          {catalogItems.map((item) => (
                            <p
                              key={item.label}
                              className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"
                            >
                              <span>
                                <FontAwesomeIcon icon={item.icon} className="mr-2 text-slate-500" />
                                {item.label}
                              </span>
                              <strong>{item.value}</strong>
                            </p>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Animate>
                )}

                {tab === "customers" && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                    <div className="mt-4 rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                      <h4 className="text-base font-extrabold text-slate-900">
                        {t("admin_dashboard.subscription_details.customers_title")}
                      </h4>
                      <div className="mt-2 max-h-96 overflow-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr className="border-b text-left text-slate-700">
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.customer_name")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.customer_phone")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.customer_email")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.customer_cpf")}</th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.customer_debit_status")}
                              </th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.customer_orders_count")}
                              </th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.customer_manual_external_orders_count")}
                              </th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.customer_manual_external_net_amount")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.customers.map((item) => (
                              <tr key={item.customerId} className="border-b last:border-0 text-slate-800">
                                <td className="py-2 px-2">{item.name || "-"}</td>
                                <td className="py-2 px-2">{item.phone || "-"}</td>
                                <td className="py-2 px-2">{item.email || "-"}</td>
                                <td className="py-2 px-2">{item.cpf || "-"}</td>
                                <td className="py-2 px-2">{getCustomerDebitStatusLabel(item.debitStatus, t)}</td>
                                <td className="py-2 px-2">{item.ordersCount}</td>
                                <td className="py-2 px-2">{item.manualExternalOrdersCount}</td>
                                <td className="py-2 px-2">{convertToLocalMoney(item.manualExternalOrdersNetAmount)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Animate>
                )}

                {tab === "employees" && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                    <div className="mt-4 rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                      <h4 className="text-base font-extrabold text-slate-900">
                        {t("admin_dashboard.subscription_details.employees_title")}
                      </h4>
                      <div className="mt-2 max-h-96 overflow-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr className="border-b text-left text-slate-700">
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.employee_name")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.employee_email")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.employee_phone")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.employee_role")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.employee_status")}</th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.employee_permissions_count")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.employees.map((item) => (
                              <tr key={item.employeeId} className="border-b last:border-0 text-slate-800">
                                <td className="py-2 px-2 font-semibold">{item.name || "-"}</td>
                                <td className="py-2 px-2">{item.email || "-"}</td>
                                <td className="py-2 px-2">{item.phone || "-"}</td>
                                <td className="py-2 px-2">
                                  {item.isMaster
                                    ? `${item.companyFunction || "-"} (${t("admin_dashboard.subscription_details.master")})`
                                    : item.companyFunction || "-"}
                                </td>
                                <td className="py-2 px-2">{getEmployeeStatusLabel(item.status, t)}</td>
                                <td className="py-2 px-2 font-bold text-slate-700">{item.permissionsCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Animate>
                )}

                {tab === "couriers" && (
                  <Animate type={AnimationTypes.fadeInTraslate} duration="1.3s">
                    <div className="mt-4 rounded-2xl border-2 border-orange-200 bg-white p-4 shadow-sm">
                      <h4 className="text-base font-extrabold text-slate-900">
                        {t("admin_dashboard.subscription_details.couriers_title")}
                      </h4>
                      <div className="mt-2 max-h-96 overflow-auto rounded-lg border border-slate-200">
                        <table className="min-w-full text-sm">
                          <thead className="bg-slate-50">
                            <tr className="border-b text-left text-slate-700">
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.courier_name")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.courier_email")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.courier_phone")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.courier_vehicle")}</th>
                              <th className="py-2 px-2">{t("admin_dashboard.subscription_details.courier_status")}</th>
                              <th className="py-2 px-2">
                                {t("admin_dashboard.subscription_details.courier_deliveries_count")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {details.couriers.map((item) => (
                              <tr key={item.courierId} className="border-b last:border-0 text-slate-800">
                                <td className="py-2 px-2 font-semibold">{item.name || "-"}</td>
                                <td className="py-2 px-2">{item.email || "-"}</td>
                                <td className="py-2 px-2">{item.phone || "-"}</td>
                                <td className="py-2 px-2">{getCourierVehicleLabel(item.vehicleType, t)}</td>
                                <td className="py-2 px-2">{getCourierStatusLabel(item.status, t)}</td>
                                <td className="py-2 px-2 font-bold text-slate-700">{item.deliveriesCount}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Animate>
                )}
              </>
            )}
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
